import React, { useState, useRef, useEffect } from "react";
import { auth } from "../firebase";
import {
  GoogleAuthProvider,
  signInWithPopup,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  sendEmailVerification,
  updateProfile,
  reload,
} from "firebase/auth";
import { Autocomplete } from "@react-google-maps/api";
import {
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Paper,
  IconButton,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  AttachMoney,
  MoneyOff,
  PhotoCamera,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase";

const categories = [
  { id: "house", label: "House Lights" },
  { id: "event", label: "Event" },
  { id: "community", label: "Community Lights" },
  { id: "market", label: "Market" },
  { id: "santa", label: "Santa Visit" },
  { id: "tree_farm", label: "Tree Farm" },
  { id: "show", label: "Show" },
  { id: "skating", label: "Ice Skating" },
  { id: "restaurant", label: "Dining" },
  { id: "shopping", label: "Shopping" },
  { id: "parade", label: "Parade" },
  { id: "charity", label: "Charity" },
];

const AddHouseForm = ({ onAddHouse, onClose }) => {
  const [user, setUser] = useState(auth.currentUser);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [description, setDescription] = useState("");
  const [hours, setHours] = useState("");
  const [url, setUrl] = useState("");
  const [category, setCategory] = useState("house");
  const [isFree, setIsFree] = useState(true);
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [selectedCoordinates, setSelectedCoordinates] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [addressAutocomplete, setAddressAutocomplete] = useState(null);
  const addressInputRef = useRef(null);
  
  // Login form states
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginName, setLoginName] = useState("");
  const [isSignup, setIsSignup] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [loginInfo, setLoginInfo] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [pendingVerificationUser, setPendingVerificationUser] = useState(null);

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // Login handlers
  const getFriendlyErrorMessage = (code) => {
    switch (code) {
      case "auth/email-already-in-use":
        return "This email is already registered. Please log in or use a different email.";
      case "auth/invalid-email":
        return "The email address is not valid. Please check and try again.";
      case "auth/user-not-found":
        return "No account found with this email. Please sign up.";
      case "auth/wrong-password":
        return "Incorrect password. Please try again.";
      case "auth/weak-password":
        return "Password should be at least 6 characters long.";
      case "auth/network-request-failed":
        return "Network error. Please check your internet connection.";
      case "auth/too-many-requests":
        return "Too many attempts. Please try again later.";
      default:
        return "An unexpected error occurred. Please try again.";
    }
  };

  const handleGoogleLogin = async () => {
    const provider = new GoogleAuthProvider();
    try {
      setLoginLoading(true);
      setLoginError("");
      await setPersistence(auth, browserLocalPersistence);
      await signInWithPopup(auth, provider);
      // User state will be updated via useEffect
    } catch (err) {
      setLoginError(getFriendlyErrorMessage(err.code));
    } finally {
      setLoginLoading(false);
    }
  };

  const handleEmailPasswordAuth = async (e) => {
    e.preventDefault();
    
    if (isSignup && !loginName.trim()) {
      setLoginError("Please enter your name");
      return;
    }
    
    try {
      setLoginLoading(true);
      setLoginError("");
      setLoginInfo("");
      await setPersistence(auth, browserLocalPersistence);
      
      if (isSignup) {
        const result = await createUserWithEmailAndPassword(
          auth,
          loginEmail,
          loginPassword
        );
        
        // Update the user's display name
        if (loginName.trim()) {
          await updateProfile(result.user, {
            displayName: loginName.trim()
          });
          // Reload user to get updated profile
          await reload(result.user);
        }
        
        await sendEmailVerification(result.user);
        setLoginInfo("Verification email sent! Please check your inbox (and spam folder) to verify your email address.");
        
        // Update user state immediately with display name
        const updatedUser = {
          ...result.user,
          displayName: loginName.trim()
        };
        setUser(updatedUser);
        setPendingVerificationUser(updatedUser);
        
        // Clear form fields
        setLoginEmail("");
        setLoginPassword("");
        setLoginName("");
      } else {
        const result = await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
        if (!result.user.emailVerified) {
          setLoginError("Please verify your email before logging in.");
          setLoginLoading(false);
          return;
        }
        // Update user state immediately
        setUser(result.user);
        // Clear form fields
        setLoginEmail("");
        setLoginPassword("");
      }
    } catch (err) {
      setLoginError(getFriendlyErrorMessage(err.code));
    } finally {
      setLoginLoading(false);
    }
  };

  // Handle address selection from autocomplete
  const handlePlaceChanged = () => {
    if (addressAutocomplete) {
      const place = addressAutocomplete.getPlace();
      if (place.geometry) {
        setSelectedCoordinates({
          latitude: place.geometry.location.lat(),
          longitude: place.geometry.location.lng(),
        });
    setAddress(place.formatted_address || "");
        
        // Auto-suggest location name from address
        // Only auto-fill if name field is empty
        if (!name.trim()) {
          let suggestedName = "";
          
          // First, try to use the place name (for businesses/POIs)
          if (place.name && place.name !== place.formatted_address) {
            suggestedName = place.name;
          } else {
            // Extract street address (first part before comma)
            const addressParts = (place.formatted_address || "").split(",");
            if (addressParts.length > 0) {
              suggestedName = addressParts[0].trim();
            }
          }
          
          // If we have a suggested name, set it
          if (suggestedName) {
            setName(suggestedName);
          }
        }
      }
    }
  };

  // Handle image selection
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    const remainingSlots = 3 - images.length;
    const filesToAdd = files.slice(0, remainingSlots);

    filesToAdd.forEach((file) => {
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImages((prev) => [...prev, file]);
          setImagePreviews((prev) => [...prev, reader.result]);
        };
        reader.readAsDataURL(file);
      }
    });
  };

  // Remove image
  const handleRemoveImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  // Upload images to Firebase Storage
  const uploadImages = async (locationId) => {
    if (images.length === 0) return [];

    setUploadingImages(true);
    const imageUrls = [];

    try {
      for (let i = 0; i < images.length; i++) {
        const image = images[i];
        const filename = `locations/${locationId}/${Date.now()}_${i}.jpg`;
        const storageRef = ref(storage, filename);
        
        // Convert file to blob
        const blob = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            fetch(reader.result)
              .then((res) => res.blob())
              .then(resolve)
              .catch(reject);
          };
          reader.onerror = reject;
          reader.readAsDataURL(image);
        });

        await uploadBytes(storageRef, blob);
        const downloadUrl = await getDownloadURL(storageRef);
        imageUrls.push(downloadUrl);
      }
    } catch (error) {
      throw error;
    } finally {
      setUploadingImages(false);
    }

    return imageUrls;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!name.trim()) {
      alert("Please enter a name for the location");
      return;
    }
    if (!address.trim()) {
      alert("Please enter an address");
      return;
    }
    if (!selectedCoordinates) {
      alert("Please select an address from the autocomplete suggestions");
      return;
    }

    // Check if user is logged in
    if (!user) {
      alert("Please log in to add a location. You'll be redirected to the login page.");
      onAddHouse(null); // This will trigger redirect in parent
      return;
    }

    setSubmitting(true);

    try {
      // Create location data (without images first)
      const locationData = {
        name: name.trim(),
        address: address.trim(),
        description: description.trim(),
        category,
        isFree,
        latitude: selectedCoordinates.latitude,
        longitude: selectedCoordinates.longitude,
        userId: user.uid,
        verified: false,
        votes: 0,
        images: [],
        createdAt: new Date().toISOString(),
        // Only include optional fields if they have values
        ...(hours.trim() && { hours: hours.trim() }),
        ...(url.trim() && { link: url.trim() }),
      };

      // Call parent handler to add location (will return location ID)
      const locationId = await onAddHouse(locationData);

      // Upload images if any
      if (images.length > 0 && locationId) {
        try {
          const imageUrls = await uploadImages(locationId);
          // Update location with image URLs
          await onAddHouse({ id: locationId, images: imageUrls }, true); // true = update mode
        } catch (imageError) {
          // Continue even if image upload fails
        }
      }

      // Reset form
      setName("");
      setAddress("");
      setDescription("");
      setHours("");
      setUrl("");
      setCategory("house");
      setIsFree(true);
      setImages([]);
      setImagePreviews([]);
      setSelectedCoordinates(null);

      // Close form
      if (onClose) {
        onClose();
      }
    } catch (error) {
      alert("Could not add location. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper
      elevation={6}
      sx={{
        maxWidth: 600,
        margin: "auto",
        padding: 4,
        position: "relative",
        borderRadius: 3,
        backgroundColor: "#fff",
        marginTop: 2,
        marginBottom: 4,
      }}
    >
      <IconButton
        onClick={onClose}
        aria-label="Close"
        sx={{
          position: "absolute",
          top: 8,
          right: 8,
          backgroundColor: "#f5f5f5",
          "&:hover": { backgroundColor: "#e0e0e0" },
        }}
      >
        <CloseIcon />
      </IconButton>

      {!user ? (
        // Show login form when not logged in
        <Box>
          <Typography variant="h5" align="center" sx={{ marginBottom: 1, fontWeight: 700 }}>
            Sign In to Add a Location
        </Typography>
          <Typography variant="body2" align="center" sx={{ marginBottom: 4, color: "text.secondary" }}>
            Please sign in to share your favorite Christmas experience with the community
        </Typography>

          {loginError && (
            <Alert severity="error" sx={{ marginBottom: 2 }}>
              {loginError}
            </Alert>
          )}
          {loginInfo && (
            <Alert 
              severity="info" 
              sx={{ marginBottom: 2 }}
              action={
                loginInfo.includes("Verification email") && (user || pendingVerificationUser) && (!user?.emailVerified && !pendingVerificationUser?.emailVerified) ? (
                  <Button
                    size="small"
                    onClick={async () => {
                      try {
                        const userToResend = user || pendingVerificationUser;
                        await sendEmailVerification(userToResend);
                        setLoginInfo("Verification email resent! Please check your inbox (and spam folder).");
                      } catch (err) {
                        setLoginError("Failed to resend verification email. Please try again.");
                      }
                    }}
                    sx={{ textTransform: "none" }}
                  >
                    Resend
                  </Button>
                ) : null
              }
            >
              {loginInfo}
            </Alert>
          )}

          <Box component="form" onSubmit={handleEmailPasswordAuth} sx={{ marginBottom: 3 }}>
            {isSignup && (
              <TextField
                label="Name"
                type="text"
                fullWidth
                margin="normal"
                value={loginName}
                onChange={(e) => setLoginName(e.target.value)}
                placeholder="Enter your name"
                required
                autoFocus
              />
            )}
            <TextField
              label="Email"
              type="email"
              fullWidth
              margin="normal"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              required
              autoFocus={!isSignup}
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              margin="normal"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              required
            />
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loginLoading}
              sx={{
                marginTop: 2,
                backgroundColor: "#8C1515",
                "&:hover": { backgroundColor: "#1A4D0E" },
              }}
            >
              {loginLoading ? <CircularProgress size={24} sx={{ color: "#fff" }} /> : (isSignup ? "Sign Up" : "Log In")}
            </Button>
          </Box>

          <Box sx={{ textAlign: "center", marginBottom: 2 }}>
            <Typography variant="body2" sx={{ marginBottom: 2, color: "text.secondary" }}>
              OR
            </Typography>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleGoogleLogin}
              disabled={loginLoading}
              sx={{
                borderColor: "#8C1515",
                color: "#8C1515",
                "&:hover": {
                  borderColor: "#1A4D0E",
                  backgroundColor: "rgba(139, 38, 53, 0.04)",
                },
              }}
            >
              Continue with Google
            </Button>
          </Box>

          <Typography align="center" sx={{ marginTop: 2 }}>
            {isSignup ? (
              <>
                Already have an account?{" "}
                <Button
                  color="primary"
                  onClick={() => {
                    setIsSignup(false);
                    setLoginError("");
                    setLoginInfo("");
                    setLoginName("");
                  }}
                  sx={{ textTransform: "none" }}
                >
                  Log In
                </Button>
              </>
            ) : (
              <>
                Don't have an account?{" "}
                <Button
                  color="primary"
                  onClick={() => {
                    setIsSignup(true);
                    setLoginError("");
                    setLoginInfo("");
                    setLoginName("");
                  }}
                  sx={{ textTransform: "none" }}
                >
                  Sign Up
                </Button>
              </>
            )}
          </Typography>
        </Box>
      ) : (
        // Show location form when logged in
        <Box>
          <Typography variant="h5" align="center" sx={{ marginBottom: 1, fontWeight: 700 }}>
            Add a Location
          </Typography>
          <Typography variant="body2" align="center" sx={{ marginBottom: 3, color: "text.secondary" }}>
            Share your favorite Christmas experience
          </Typography>

          <form onSubmit={handleSubmit}>
        <Typography variant="h6" sx={{ marginTop: 2, marginBottom: 2, fontWeight: 600 }}>
          Basic Information
        </Typography>

        {/* Name Input */}
        <TextField
          label="Location Name *"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Smith Family Spectacular Lights"
          fullWidth
          required
          sx={{ marginBottom: 3 }}
        />

        {/* Address Input with Autocomplete */}
        <Box sx={{ marginBottom: 3 }}>
          <Autocomplete
            onLoad={(autocomplete) => setAddressAutocomplete(autocomplete)}
            onPlaceChanged={handlePlaceChanged}
          >
            <TextField
              inputRef={addressInputRef}
              label="Address *"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Start typing your address..."
              fullWidth
              required
            />
          </Autocomplete>
          {selectedCoordinates && (
            <Typography variant="caption" color="success.main" sx={{ marginTop: 0.5, display: "block" }}>
              ✓ Address verified
            </Typography>
          )}
          {!selectedCoordinates && address && (
            <Typography variant="caption" color="text.secondary" sx={{ marginTop: 0.5, display: "block" }}>
              Start typing and select from suggestions
            </Typography>
          )}
        </Box>

        <Typography variant="h6" sx={{ marginTop: 3, marginBottom: 2, fontWeight: 600 }}>
          Category & Type
        </Typography>

        {/* Category Selection */}
        <FormControl fullWidth sx={{ marginBottom: 3 }}>
          <InputLabel id="category-label">What type of location is this? *</InputLabel>
          <Select
            labelId="category-label"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            label="What type of location is this? *"
            fullWidth
          >
            {categories.map((cat) => (
              <MenuItem key={cat.id} value={cat.id}>
                {cat.label}
            </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Free/Paid Toggle */}
        <Box sx={{ marginBottom: 3 }}>
          <Typography variant="body2" sx={{ marginBottom: 1, fontWeight: 600 }}>
            Is it free or paid? *
          </Typography>
          <ToggleButtonGroup
            value={isFree}
            exclusive
            onChange={(e, newValue) => {
              if (newValue !== null) setIsFree(newValue);
            }}
            fullWidth
            sx={{
              "& .MuiToggleButton-root": {
                flex: 1,
                padding: "12px",
                border: "1px solid #e0e0e0",
                "&.Mui-selected": {
                  backgroundColor: "#8B2635",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "#6B1F1F",
                  },
                },
              },
            }}
          >
            <ToggleButton value={true}>
              <MoneyOff sx={{ marginRight: 1 }} />
              Free
            </ToggleButton>
            <ToggleButton value={false}>
              <AttachMoney sx={{ marginRight: 1 }} />
              Paid
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Typography variant="h6" sx={{ marginTop: 3, marginBottom: 2, fontWeight: 600 }}>
          Additional Details
        </Typography>

        {/* Description Input */}
        <TextField
          label="Description (Optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What makes this location special? Any tips for visitors?"
          multiline
          rows={4}
          fullWidth
          sx={{ marginBottom: 3 }}
          inputProps={{ maxLength: 500 }}
          helperText={`${description.length} / 500 characters`}
        />

        {/* Hours Input */}
        <TextField
          label="Hours (Optional)"
          value={hours}
          onChange={(e) => setHours(e.target.value)}
          placeholder="e.g., Daily 9:00 AM - 9:00 PM or See website for hours"
          fullWidth
          sx={{ marginBottom: 3 }}
          helperText="Enter operating hours, dates, or special holiday hours"
        />

        {/* Website URL */}
        <TextField
          label="Website URL (Optional)"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="e.g., https://example.com or www.example.com"
          type="url"
          fullWidth
          sx={{ marginBottom: 3 }}
          helperText="Add a website link for events, businesses, or more information"
        />

        {/* Images Section */}
        <Box sx={{ marginBottom: 3 }}>
          <Typography variant="body2" sx={{ marginBottom: 1, fontWeight: 600 }}>
            Photos (Optional - Up to 3)
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ marginBottom: 2, display: "block" }}>
            Add photos to showcase this location
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {imagePreviews.map((preview, index) => (
              <Box
                key={index}
                sx={{
                  position: "relative",
                  width: 120,
                  height: 120,
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "2px solid #e0e0e0",
                }}
              >
                <img
                  src={preview}
                  alt={`Preview ${index + 1}`}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
                <IconButton
                  size="small"
                  onClick={() => handleRemoveImage(index)}
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                    color: "#fff",
                    "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.9)" },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}

            {images.length < 3 && (
              <Button
                variant="outlined"
                component="label"
                startIcon={<PhotoCamera />}
                sx={{
                  width: 120,
                  height: 120,
                  borderStyle: "dashed",
                  borderWidth: 2,
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                Add Photo
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleImageSelect}
                  multiple
                />
              </Button>
            )}
          </Box>
        </Box>

        {/* Submit Button */}
        <Box textAlign="center" sx={{ marginTop: 4 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting || uploadingImages}
            sx={{
              backgroundColor: "#8B2635",
              color: "#fff",
              padding: "12px 32px",
              fontSize: "16px",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "#6B1F1F",
              },
              "&:disabled": {
                backgroundColor: "#ccc",
              },
            }}
          >
            {uploadingImages ? (
              <>
                <CircularProgress size={20} sx={{ marginRight: 1, color: "#fff" }} />
                Uploading Images...
              </>
            ) : submitting ? (
              <>
                <CircularProgress size={20} sx={{ marginRight: 1, color: "#fff" }} />
                Adding Location...
              </>
            ) : (
              "Add Location"
            )}
          </Button>
        </Box>
      </form>
        </Box>
      )}
    </Paper>
  );
};

export default AddHouseForm;
